/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import ButtonStyled from "../../components/common/ButtonStyled";
import InventoryTab from "./_components/InventoryTab";
import ItemInventory from "./_components/ItemInventory";
import { useSelector } from "react-redux";
import { selectClientPermissions } from "../../features/auth/authSlice";
import { IconPackage, IconBox } from "@tabler/icons-react";

interface TabConfig {
  key: string;
  label: string;
  component: (portalContainer: HTMLDivElement | null) => React.ReactNode;
  module: string;
  operation: string;
  icon: React.ReactNode;
}

const INVENTORY_SUB_TABS: TabConfig[] = [
  {
    key: "ShipmentInventory",
    label: "Shipment Inventory",
    component: (portalContainer) => (
      <InventoryTab portalContainer={portalContainer} />
    ),
    module: "shipment_inventory",
    operation: "read",
    icon: <IconPackage size={18} />,
  },
  {
    key: "ItemsInventory",
    label: "Item Inventory",
    component: (portalContainer) => (
      <ItemInventory portalContainer={portalContainer} />
    ),
    module: "product_inventory",
    operation: "read",
    icon: <IconBox size={18} />,
  },
];

interface InventoryManagementProps {
  setHeaderButtons?: (n: React.ReactNode) => void;
}

const InventoryMangement: React.FC<InventoryManagementProps> = ({ setHeaderButtons }) => {
  const permissions = useSelector(selectClientPermissions) || [];
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setHeaderButtons?.(null);
  }, [setHeaderButtons]);

  // 🔐 Filter tabs based on permission
  const allowedTabs = useMemo(() => {
    if (!permissions || permissions.length === 0) return INVENTORY_SUB_TABS;
    return INVENTORY_SUB_TABS.filter((tab) =>
      permissions.some(
        (p) =>
          p.module === tab.module &&
          p.operations.includes(tab.operation),
      ),
    );
  }, [permissions]);

  // 🧠 Active tab with persistence
  const [activeRole, setActiveRole] = useState<string>(() => {
    const saved = localStorage.getItem("InventoryActiveRole");
    return saved && allowedTabs.some((t) => t.key === saved || t.label === saved)
      ? (allowedTabs.find((t) => t.key === saved || t.label === saved)?.key || allowedTabs[0]?.key)
      : allowedTabs[0]?.key;
  });

  // 🔄 Fix active tab if permission changes
  useEffect(() => {
    if (!allowedTabs.find((t) => t.key === activeRole)) {
      setActiveRole(allowedTabs[0]?.key);
    }
  }, [allowedTabs]);

  useEffect(() => {
    if (activeRole) {
      localStorage.setItem("InventoryActiveRole", activeRole);
    }
  }, [activeRole]);

  const activeTab = allowedTabs.find((t) => t.key === activeRole);

  // ⛔ No permission case
  if (allowedTabs.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        You don't have access to this section
      </div>
    );
  }

  return (
    <div className="animate-fadeIn w-full" style={{ fontFamily: '"Inter Tight", sans-serif' }}>
      <div className="p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-lg w-full space-y-3 relative">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        {/* Header with Sub-tabs on left, controls on right */}
        <header className="flex items-center justify-between w-full flex-wrap gap-2 relative z-10">
          {allowedTabs.length > 1 ? (
            <div className="flex items-center gap-2 py-1 flex-wrap">
              {allowedTabs.map((t) => {
                const isActive = activeRole === t.key;
                return (
                  <ButtonStyled
                    key={t.key}
                    onClick={() => setActiveRole(t.key)}
                    variant={isActive ? "solid" : "outline"}
                    icon={t.icon as React.ReactElement}
                    value={t.label}
                  />
                );
              })}
            </div>
          ) : (
            <div />
          )}
          <div ref={setPortalContainer} className="flex items-center gap-2.5 ml-auto" />
        </header>

        {/* Tab Content */}
        <div className="animate-slideUp relative z-10">
          {activeTab?.component(portalContainer)}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InventoryMangement;
